import { FileText, BookOpenText, ExternalLink } from "lucide-react";

/**
 * SectorResources — renders the curated "Official Syllabus & Previous Year Papers"
 * archive section on each /sectors/:slug page. Two clean columns: Syllabus + Past Papers.
 */
export default function SectorResources({ resources, sectorName = "" }) {
  if (!resources || !Array.isArray(resources.items) || !resources.items.length) {
    return null;
  }

  const syllabus = resources.items.filter((r) => r.kind === "syllabus");
  const papers = resources.items.filter((r) => r.kind === "papers");

  return (
    <section
      className="bg-white border border-slate-200 rounded-xl p-5 md:p-7 mt-8"
      aria-labelledby="sector-resources-h2"
      data-testid="sector-resources"
    >
      <h2
        id="sector-resources-h2"
        className="text-xl md:text-2xl font-extrabold text-slate-900 mb-2"
        style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}
      >
        Official Syllabus &amp; Previous Year Papers
      </h2>
      {resources.intro && (
        <p className="text-sm text-slate-700 leading-relaxed mb-5">{resources.intro}</p>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        {/* Syllabus column */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-orange-100 text-orange-700">
              <BookOpenText className="w-4 h-4" />
            </span>
            <h3 className="text-base md:text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              Official Syllabus
            </h3>
          </div>
          <ul className="space-y-2" data-testid="sector-syllabus-list">
            {syllabus.map((r, i) => (
              <li key={i}>
                <a
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block px-3 py-3 rounded-md border border-slate-200 bg-slate-50 hover:bg-orange-50 hover:border-orange-200 transition-colors group"
                  data-testid={`sector-syllabus-link-${i}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="text-sm font-semibold text-slate-900 group-hover:text-orange-700 leading-snug">
                      {r.label}
                    </div>
                    <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-orange-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                  </div>
                  {r.note && (
                    <div className="text-xs text-slate-600 mt-1 leading-relaxed">{r.note}</div>
                  )}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Previous papers column */}
        <div>
          <div className="flex items-center gap-2 mb-3">
            <span className="inline-grid place-items-center w-8 h-8 rounded-md bg-orange-100 text-orange-700">
              <FileText className="w-4 h-4" />
            </span>
            <h3 className="text-base md:text-lg font-bold text-slate-900" style={{ fontFamily: "'Cabinet Grotesk',sans-serif" }}>
              Previous Year Papers
            </h3>
          </div>
          {papers.length ? (
            <ul className="space-y-2" data-testid="sector-papers-list">
              {papers.map((r, i) => (
                <li key={i}>
                  <a
                    href={r.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="block px-3 py-3 rounded-md border border-slate-200 bg-slate-50 hover:bg-orange-50 hover:border-orange-200 transition-colors group"
                    data-testid={`sector-papers-link-${i}`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900 group-hover:text-orange-700 leading-snug">
                        {r.label}
                      </div>
                      <ExternalLink className="w-3.5 h-3.5 text-slate-400 group-hover:text-orange-600 flex-shrink-0 mt-0.5" aria-hidden="true" />
                    </div>
                    {r.note && (
                      <div className="text-xs text-slate-600 mt-1 leading-relaxed">{r.note}</div>
                    )}
                  </a>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-slate-500">
              Question paper archive is available inside each {sectorName || "sector"} notification PDF on the official portal.
            </p>
          )}
        </div>
      </div>

      <p className="mt-5 text-[11px] text-slate-500 leading-relaxed">
        All resources link to the official government portals (.gov.in / .nic.in / .in). hireds.in does not host third-party PDFs to keep the archive accurate and copyright-safe.
      </p>
    </section>
  );
}
